/*
  LOQI gift picker — behaviour layer.

  Loaded unconditionally (defer) on every page via the cart sections, so the
  delegated click/keyboard handlers are ALWAYS installed regardless of cart
  state. (Rendering this inline inside the cart snippet broke selection: when a
  shopper started with an empty cart, the snippet rendered nothing on initial
  load, and the later AJAX cart re-render injects markup via innerHTML — which
  does NOT execute <script> tags — so the handlers were never attached.)

  GIFT vs PURCHASE
  The picker adds its tote with a hidden line-item property `_loqi_gift: true`.
  Every "is this the gift?" check keys off that tag — so a tote a shopper adds
  normally (no tag, e.g. from the PDP) is treated as a regular purchase: it is
  never auto-removed, counts toward the $250, and shows its price.

  All UI state is read from the DOM (the hidden [data-loqi-gift] holder + the
  [data-loqi-choose] cards), so this file carries no Liquid.
*/
(function () {
  if (window.__loqiGiftPickerInstalled) return;
  window.__loqiGiftPickerInstalled = true;

  var GIFT_PROP = '_loqi_gift';
  var ADD_URL = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
  var CHANGE_URL = (window.routes && window.routes.cart_change_url) || '/cart/change.js';
  var UPDATE_URL = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
  var changingGift = false;
  var request = function (url, options) {
    return window.WWCartSafety ? window.WWCartSafety.fetchWithTimeout(url, options) : fetch(url, options);
  };

  function holder() { return document.querySelector('[data-loqi-gift]'); }
  function threshold() { var h = holder(); return h ? (parseInt(h.getAttribute('data-threshold'), 10) || 25000) : 25000; }
  function instockCount() { var h = holder(); return h ? (parseInt(h.getAttribute('data-instock'), 10) || 0) : 0; }

  function isGiftLine(item) {
    return !!(item && item.properties && item.properties[GIFT_PROP]);
  }

  function sectionsToRender() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      return drawer.getSectionsToRender().map(function (s) { return s.id; });
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  function applyRender(response) {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents(response);
    } else {
      window.WWCartSafety?.fail();
      return;
    }
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'loqi-gift-picker', cartData: response });
      }
    } catch (_) {}
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  // Ensure exactly one gift line (tagged _loqi_gift): remove any existing
  // gift-tagged lines, then add the chosen variant tagged as a gift.
  // variantId null = just remove the gift. Only TAGGED lines are ever touched,
  // so a tote the shopper is buying (untagged) is never removed.
  function setGift(variantId) {
    if (changingGift) return;
    changingGift = true;
    var operation = window.WWCartSafety?.begin(variantId ? 'add' : 'change');
    return request('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var giftLines = (cart.items || []).filter(isGiftLine);

        // Remove existing gift lines one at a time (by line key).
        var chain = Promise.resolve();
        giftLines.forEach(function (line) {
          chain = chain.then(function () {
            return request(CHANGE_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: line.key, quantity: 0 })
            });
          });
        });

        return chain.then(function () {
          var sections = sectionsToRender().join(',');
          if (variantId) {
            var props = {};
            props[GIFT_PROP] = 'true';
            return request(ADD_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
              body: JSON.stringify({
                items: [{ id: parseInt(variantId, 10), quantity: 1, properties: props }],
                sections: sections,
                sections_url: window.location.pathname
              })
            }).then(function (r) { return r.json(); }).then(applyRender);
          }
          // Removal only — fetch fresh sections via a no-op update and re-render.
          return request(UPDATE_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ updates: {}, sections: sections, sections_url: window.location.pathname })
          }).then(function (r) { return r.json(); }).then(applyRender);
        });
      })
      .catch(function () { window.WWCartSafety?.fail(); })
      .finally(function () {
        changingGift = false;
        document.querySelectorAll('[data-loqi-choose]').forEach(function (c) { c.style.pointerEvents = ''; });
        if (operation) window.WWCartSafety.finish(operation);
      });
  }

  // The whole card is the target: click (or Enter/Space) anywhere on a bag
  // selects/swaps; on the already-selected bag it removes it.
  function chooseFrom(target) {
    if (changingGift || window.WWCartSafety?.isBlocked()) return;
    var card = target.closest && target.closest('[data-loqi-choose]');
    if (!card) return;
    var variantId = card.getAttribute('data-variant-id');
    if (!variantId) return;
    document.querySelectorAll('[data-loqi-choose]').forEach(function (c) { c.style.pointerEvents = 'none'; });
    if (card.classList.contains('is-selected')) {
      setGift(null); // deselect / remove the gift
    } else {
      setGift(variantId); // select, or swap from the current gift
    }
  }

  document.addEventListener('click', function (e) {
    if (!(e.target.closest && e.target.closest('[data-loqi-choose]'))) return;
    e.preventDefault();
    chooseFrom(e.target);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    if (!(e.target.closest && e.target.closest('[data-loqi-choose]'))) return;
    e.preventDefault();
    chooseFrom(e.target);
  });

  // Qualifying subtotal excludes ONLY the free gift; a purchased tote counts.
  function qualifyingCents(cart) {
    var sum = 0;
    (cart.items || []).forEach(function (item) {
      if (!isGiftLine(item)) sum += item.original_line_price;
    });
    return sum;
  }

  // Keep the top-of-cart progress nudge in sync. The drawer header is NOT
  // re-rendered by the native quantity stepper, so we update it by hand:
  // amount, bar width, and hide it once the cart qualifies (or all sold out).
  function updateTopNudge(cart) {
    var top = document.querySelector('[data-loqi-top]');
    if (!top) return;
    var t = threshold();
    var qualifying = qualifyingCents(cart);
    if (instockCount() <= 0 || qualifying >= t) {
      top.hidden = true;
      return;
    }
    top.hidden = false;
    var remaining = t - qualifying;
    var amt = top.querySelector('[data-loqi-amount]');
    if (amt) amt.textContent = '$' + (remaining / 100).toFixed(2); // US-only market => USD
    var fill = top.querySelector('.loqi-gw__fill');
    if (fill) fill.style.width = Math.min(100, Math.round(qualifying * 100 / t)) + '%';
  }

  // Safety net: if the cart drops below $250 while the FREE GIFT is still in it,
  // remove it (the discount would stop applying and the bag revert to full
  // price). Only the tagged gift is considered — purchased totes are left alone.
  function autoRemove(cart) {
    var qualifying = 0, giftInCart = false;
    (cart.items || []).forEach(function (item) {
      if (isGiftLine(item)) { giftInCart = true; }
      else { qualifying += item.original_line_price; }
    });
    if (giftInCart && qualifying < threshold()) setGift(null);
  }

  function syncFromCart(cart) {
    if (!cart || !cart.items) return;
    updateTopNudge(cart);
    autoRemove(cart);
  }

  // cart:updated carries the cart JSON (native qty stepper / add-to-cart);
  // cart:refresh (Monk / our own) has no payload, so fetch the cart.
  function refresh(cart) {
    if (cart && cart.items) { syncFromCart(cart); return; }
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(syncFromCart)
      .catch(function () {});
  }

  document.addEventListener('cart:updated', function (e) {
    if (window.WWCartSafety && !e.detail?._wwCartConfirmed) return;
    refresh(e && e.detail);
  });
  document.addEventListener('cart:refresh', function () { if (!window.WWCartSafety) refresh(); });
  document.addEventListener('DOMContentLoaded', function () { refresh(); });
  refresh();
})();
